import type { Campaign } from '@discovr/types';
import {
  CONFIRMED_LANDING_TAB,
  DEFAULT_CAMPAIGN_TAB,
  LIVE_CAMPAIGN_LANDING_TAB,
  type CampaignTab,
} from './search';

export interface CampaignLandingInput {
  /** Undefined or null until a campaign header is available. */
  campaign: Pick<Campaign, 'first_live_at'> | null | undefined;
  /** The tabs this viewer's nav offers. */
  navItems: ReadonlyArray<{ id: string }>;
  /** The URL carries roster params (`?panel`, `?creator`, `?video`, `?focus`, `?pill`,
   *  `?page`, `?sort_by`), which only the default tab understands. */
  hasRosterLink: boolean;
  /** Creators tab total; `null` while unknown. */
  creatorsCount: number | null;
  /** Confirmed tab total; `null` while unknown. */
  confirmedCount: number | null;
}

/**
 * Tab a campaign opens on when the URL names none; `null` while that can't be
 * decided yet (missing campaign or a count still loading).
 *
 * Order: roster link → Creators, live campaign with Reporting in the nav →
 * Reporting, Creators with creators → Creators, empty Creators with Confirmed
 * in the nav and creators → Confirmed, otherwise Creators. A landing tab is only
 * ever chosen from the viewer's nav.
 */
export function resolveCampaignLandingTab({
  campaign,
  navItems,
  hasRosterLink,
  creatorsCount,
  confirmedCount,
}: CampaignLandingInput): CampaignTab | null {
  if (hasRosterLink) return DEFAULT_CAMPAIGN_TAB;
  if (!campaign) return null;
  const hasTab = (tab: CampaignTab) => navItems.some((item) => item.id === tab);
  if (campaign.first_live_at && hasTab(LIVE_CAMPAIGN_LANDING_TAB)) return LIVE_CAMPAIGN_LANDING_TAB;
  if (creatorsCount === null) return null;
  if (creatorsCount > 0) return DEFAULT_CAMPAIGN_TAB;
  if (!hasTab(CONFIRMED_LANDING_TAB)) return DEFAULT_CAMPAIGN_TAB;
  if (confirmedCount === null) return null;
  return confirmedCount > 0 ? CONFIRMED_LANDING_TAB : DEFAULT_CAMPAIGN_TAB;
}
